export interface StrategyState {
  strategyStateId: string;
  tenantId: string;
  version: number;
  timeHorizonMonths: number;
  priorities: {
    costEfficiency: number;
    customerExperience: number;
    speedToExecute: number;
    standardization: number;
  };
  constraints: {
    maxNetHeadcountChange?: number;
    maxFieldLayers?: number;
    maxStoreLayers?: number;
    maxLaborCostIncreasePct?: number;
  };
  narrative: string;
  requiredCapabilityIds?: string[];
}

export type StrategyStateUpsert = Omit<StrategyState, 'strategyStateId' | 'version'> & {
  strategyStateId?: string;
  version?: number;
};

const isNumber = (value: unknown): value is number => typeof value === 'number' && !Number.isNaN(value);

const validatePriorityValue = (value: unknown) => isNumber(value) && value >= 0 && value <= 1;

const validateConstraints = (input: any) => {
  if (input === undefined || input === null) return false;
  const allowedKeys = [
    'maxNetHeadcountChange',
    'maxFieldLayers',
    'maxStoreLayers',
    'maxLaborCostIncreasePct',
  ];
  return (
    typeof input === 'object' &&
    Object.keys(input).every((key) => allowedKeys.includes(key)) &&
    (input.maxNetHeadcountChange === undefined || (Number.isInteger(input.maxNetHeadcountChange) && Math.abs(input.maxNetHeadcountChange) < Number.MAX_SAFE_INTEGER)) &&
    (input.maxFieldLayers === undefined || Number.isInteger(input.maxFieldLayers)) &&
    (input.maxStoreLayers === undefined || Number.isInteger(input.maxStoreLayers)) &&
    (input.maxLaborCostIncreasePct === undefined || isNumber(input.maxLaborCostIncreasePct))
  );
};

const buildError = (message: string) => new Error(message);

const validateVersion = (value: unknown) =>
  value === undefined || (typeof value === 'number' && Number.isInteger(value) && value > 0);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

export const StrategyStateSchema = {
  parse(input: unknown): StrategyState {
    const version = (input as any)?.version;
    if (!validateVersion(version) || version === undefined) {
      throw buildError('version must be a positive integer');
    }
    const result = StrategyStateUpsertSchema.safeParse(input);
    if (!result.success) {
      throw result.error;
    }
    if (!result.data.strategyStateId) {
      throw buildError('strategyStateId is required');
    }
    return {
      ...(result.data as StrategyStateUpsert),
      strategyStateId: result.data.strategyStateId!,
      version,
    };
  },
  safeParse(input: unknown): { success: true; data: StrategyState } | { success: false; error: Error } {
    try {
      const data = this.parse(input);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, error: error as Error };
    }
  },
};

export const StrategyStateUpsertSchema = {
  parse(input: unknown): StrategyStateUpsert {
    if (typeof input !== 'object' || input === null) {
      throw buildError('Payload must be an object.');
    }
    const data = input as Record<string, any>;
    if (!data.tenantId || typeof data.tenantId !== 'string') {
      throw buildError('tenantId is required.');
    }
    if (!isNumber(data.timeHorizonMonths) || !Number.isInteger(data.timeHorizonMonths) || data.timeHorizonMonths <= 0) {
      throw buildError('timeHorizonMonths must be a positive integer.');
    }
    const priorities = data.priorities;
    if (
      !priorities ||
      typeof priorities !== 'object' ||
      !validatePriorityValue(priorities.costEfficiency) ||
      !validatePriorityValue(priorities.customerExperience) ||
      !validatePriorityValue(priorities.speedToExecute) ||
      !validatePriorityValue(priorities.standardization)
    ) {
      throw buildError('priorities must include costEfficiency, customerExperience, speedToExecute, and standardization between 0 and 1.');
    }
    if (!validateConstraints(data.constraints)) {
      throw buildError('constraints must include only numeric constraint fields.');
    }
    if (typeof data.narrative !== 'string') {
      throw buildError('narrative is required.');
    }
    if (data.requiredCapabilityIds !== undefined && !isStringArray(data.requiredCapabilityIds)) {
      throw buildError('requiredCapabilityIds must be an array of strings when provided.');
    }
    if (data.strategyStateId !== undefined && typeof data.strategyStateId !== 'string') {
      throw buildError('strategyStateId must be a string when provided.');
    }
    if (!validateVersion(data.version)) {
      throw buildError('version must be a positive integer when provided.');
    }
    return {
      strategyStateId: data.strategyStateId,
      tenantId: data.tenantId,
      timeHorizonMonths: data.timeHorizonMonths,
      priorities: {
        costEfficiency: priorities.costEfficiency,
        customerExperience: priorities.customerExperience,
        speedToExecute: priorities.speedToExecute,
        standardization: priorities.standardization,
      },
      constraints: {
        maxNetHeadcountChange: data.constraints.maxNetHeadcountChange,
        maxFieldLayers: data.constraints.maxFieldLayers,
        maxStoreLayers: data.constraints.maxStoreLayers,
        maxLaborCostIncreasePct: data.constraints.maxLaborCostIncreasePct,
      },
      narrative: data.narrative,
      version: data.version,
      requiredCapabilityIds: data.requiredCapabilityIds,
    };
  },
  safeParse(input: unknown): { success: true; data: StrategyStateUpsert } | { success: false; error: Error } {
    try {
      const data = this.parse(input);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, error: error as Error };
    }
  },
};
